import { Button, DropdownItem, HFlow, Icon, Tooltip, useTheme } from 'bold-ui'
import { useSession } from 'components/auth/useSession'
import { useErrorHandler } from 'components/error'
import { ListFieldStatus } from 'components/form'
import { usePrinter } from 'components/print'
import { PrinterType } from 'components/print/model-print'
import { useAuditaImpressaoOrientacaoMutation } from 'graphql/hooks.generated'
import { getEditableItemRemoveTooltipText } from 'view/atendimentos/atendimento-individual/atendimento-observacao/util-atendObservacao'
import { CidadaoAtendimento } from 'view/atendimentos/types/CidadaoAtendimento'
import { removeWithOptionalConfirm } from 'view/retificacao-atendimento/removeWithOptionalConfirm'

import { grupoCboOrientacoes } from '../../acessos'
import { OrientacaoRowModel } from '../model-orientacoes'
import { ImpressaoOrientacoes } from './ImpressaoOrientacoes'

interface OrientacoesTableActionsProps {
  item: OrientacaoRowModel
  atendimentoId: ID
  prontuarioId: ID
  cidadao: CidadaoAtendimento
  readOnly?: boolean
  controlModifiedStatus?: boolean
  onRemove(itemToRemove: OrientacaoRowModel): void
  onReset(itemToReset: OrientacaoRowModel): void
  onEditClick(cacheId: ID): void
}

export function OrientacoesTableActions(props: Readonly<OrientacoesTableActionsProps>) {
  const {
    item,
    cidadao,
    prontuarioId,
    atendimentoId,
    readOnly = false,
    controlModifiedStatus = false,
    onEditClick,
    onRemove,
    onReset,
  } = props

  const { hasCboAuth } = useSession({ fetchPolicy: 'cache-only' })

  /**
   * Removemos o CheckPermission e comecamos a usar o hook useSession porque ele estava causando um warning no console
   * A issue #8503 contem mais detalhes sobre o warning
   */
  const hasPermissionAdicionarOrientacoes = hasCboAuth(grupoCboOrientacoes.adicionar)

  const { printPDF } = usePrinter()
  const handleRejection = useErrorHandler()

  const theme = useTheme()

  const handleEditClick = () => {
    onEditClick(item._id)
  }

  const handleReset = () => onReset(item)

  const [auditImpressao] = useAuditaImpressaoOrientacaoMutation()

  const handlePrint = () => {
    const infoProfissional: PrinterType = {
      acesso: item.atendimentoProfissional.lotacao,
      profissional: item.atendimentoProfissional.lotacao.profissional,
    }

    const docBody = ImpressaoOrientacoes({
      ...item,
      cidadao,
    })

    printPDF(
      {
        docBody,
        documentTitle: 'ImpressaoOrientação-' + (cidadao.nomeSocial ?? cidadao.nome),
        session: infoProfissional,
      },
      handleRejection
    )
    auditImpressao({ variables: { prontuarioId, atendimentoId, orientacaoId: item.id } })
  }

  const handleExcluirOrientacaoButton = () =>
    removeWithOptionalConfirm({
      shouldAskConfirmation: controlModifiedStatus,
      title: 'Deseja excluir esta Orientação?',
      onRemove: () => onRemove(item),
    })

  const isItemFromDatabase = !!item.id

  const { isEditable, tooltipText: removeTooltipText } = getEditableItemRemoveTooltipText(
    item,
    isItemFromDatabase ? 'Não é permitido excluir uma orientação cadastrada em outro atendimento.' : 'Excluir'
  )

  const editTooltipText = isItemFromDatabase
    ? 'Não é permitido alterar uma orientação cadastrada em outro atendimento.'
    : 'Editar'

  return controlModifiedStatus ? (
    <ListFieldStatus status={item.status} onReset={handleReset} onRemove={handleExcluirOrientacaoButton}>
      <DropdownItem onClick={handlePrint}>
        <HFlow alignItems='center' hSpacing={0.5}>
          Imprimir
        </HFlow>
      </DropdownItem>
      {hasPermissionAdicionarOrientacoes && !readOnly && (
        <DropdownItem
          onClick={() => {
            handleEditClick()
          }}
          disabled={isItemFromDatabase}
        >
          <HFlow alignItems='center' hSpacing={0.5}>
            Editar
          </HFlow>
        </DropdownItem>
      )}
    </ListFieldStatus>
  ) : (
    <HFlow hSpacing={0.5} alignItems='center' justifyContent='flex-end'>
      {!isItemFromDatabase && (
        <Tooltip text='Registrado agora' data-testid='Orientacao.RegistradoAgora'>
          <Icon icon='clockOutline' color={theme.pallete.primary.c40} size={1} />
        </Tooltip>
      )}
      <HFlow hSpacing={0} alignItems='center' justifyContent='flex-end'>
        <Tooltip text='Imprimir'>
          <Button
            data-testid='ButtonOrientacao.Imprimir'
            size='small'
            kind='normal'
            skin='ghost'
            onClick={() => {
              handlePrint()
            }}
          >
            <Icon icon='printerOutline' />
          </Button>
        </Tooltip>
        {hasPermissionAdicionarOrientacoes && !readOnly && (
          <>
            <Tooltip text={editTooltipText}>
              <Button
                data-testid='ButtonOrientacao.Editar'
                size='small'
                kind='normal'
                skin='ghost'
                onClick={() => {
                  handleEditClick()
                }}
                disabled={isItemFromDatabase}
              >
                <Icon icon='penOutline' />
              </Button>
            </Tooltip>
            <Tooltip text={removeTooltipText}>
              <Button
                data-testid='ButtonOrientacao.Remover'
                size='small'
                kind='normal'
                skin='ghost'
                onClick={() => handleExcluirOrientacaoButton()}
                disabled={isItemFromDatabase || isEditable}
              >
                <Icon icon='trashOutline' />
              </Button>
            </Tooltip>
          </>
        )}
      </HFlow>
    </HFlow>
  )
}
